'use client';

import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider as NextThemesProvider } from 'next-themes';

import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';

// Client provider boundary mounted once in the root layout. Branding is NOT a
// provider here: it is resolved server-side and injected as --commune-* custom
// properties on <html> (ADR-0017), so the client tree only carries theme,
// data-fetching and tooltip context.
export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    // Light-first (ADR-0022 Q4): dark mode is deferred (brief §11), so we pin
    // the light theme and do NOT follow the OS — an OS-dark visitor must not
    // land on a half-designed dark theme. Drop forcedTheme + restore enableSystem
    // here once the dark palette is properly cadré.
    <NextThemesProvider
      attribute="class"
      defaultTheme="light"
      forcedTheme="light"
      enableSystem={false}
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delay={200}>{children}</TooltipProvider>
      </QueryClientProvider>
      <Toaster />
    </NextThemesProvider>
  );
}
